import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  CalendarToday,
  WorkspacePremium,
  CheckCircle,
  Cancel,
  AccessTime,
  ChatBubble,
  Send,
  Inbox,
  Group,
  Payment,
} from "@mui/icons-material";
import { api_urls } from "../../../utils/api-urls";



// Helper function to get full profile image URL
const getProfileImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return 'https://api.bharatdarshan.app' + url;
};

const MilanSathiUserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${api_urls}api/admins/users/${userId}/details`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message || "Failed to fetch user details");
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setError("Error fetching user details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  const { user, planExpiryInfo, connections, chatActivity, transactions } = data || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          User Details
        </Typography>
      </Box>

      {/* User Info Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              src={getProfileImageUrl(user?.profileImageUrl)}
              sx={{ width: 100, height: 100, fontSize: 40 }}
            >
              {user?.name?.charAt(0) || "U"}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" fontWeight="bold">
              {user?.name || "N/A"}
            </Typography>
            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
              <Chip
                icon={<Email sx={{ fontSize: 16 }} />}
                label={user?.email || "N/A"}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<Phone sx={{ fontSize: 16 }} />}
                label={user?.phone || "N/A"}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<CalendarToday sx={{ fontSize: 16 }} />}
                label={`Joined: ${formatDate(user?.createdAt)}`}
                size="small"
                variant="outlined"
              />
            </Box>
            <Box display="flex" gap={1} mt={1}>
              {user?.isPremium && (
                <Chip
                  icon={<WorkspacePremium />}
                  label="Premium"
                  size="small"
                  color="warning"
                />
              )}
              {user?.isVerified && (
                <Chip
                  icon={<CheckCircle />}
                  label="Verified"
                  size="small"
                  color="success"
                />
              )}
              {user?.isDocumentVerified && (
                <Chip
                  icon={<CheckCircle />}
                  label="Docs Verified"
                  size="small"
                  color="info"
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        {/* Plan Info */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <WorkspacePremium sx={{ fontSize: 30, color: "#ff9800", mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Plan Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Current Plan
              </Typography>
              <Typography variant="h6" fontWeight="bold" color={user?.planName ? "success.main" : "error.main"}>
                {user?.planName || "No Plan"}
              </Typography>
              {planExpiryInfo && (
                <>
                  <Box mt={2}>
                    <Typography variant="body2" color="textSecondary">
                      Expiry Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(planExpiryInfo.expiryDate)}
                    </Typography>
                  </Box>
                  <Box mt={2}>
                    <Typography variant="body2" color="textSecondary">
                      Days Remaining
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        color={planExpiryInfo.isExpired ? "error.main" : planExpiryInfo.daysRemaining < 7 ? "warning.main" : "success.main"}
                      >
                        {planExpiryInfo.isExpired ? "EXPIRED" : planExpiryInfo.daysRemaining}
                      </Typography>
                      {!planExpiryInfo.isExpired && <Typography variant="body2">days</Typography>}
                    </Box>
                    {!planExpiryInfo.isExpired && (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((planExpiryInfo.daysRemaining / 30) * 100, 100)}
                        color={planExpiryInfo.daysRemaining < 7 ? "warning" : "success"}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </>
              )}
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  Connection Limit
                </Typography>
                <Typography variant="body1">
                  {user?.connectionsUsed || 0} / {user?.connectionLimit || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Connection Stats */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Group sx={{ fontSize: 30, color: "#2196f3", mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Connections
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#e3f2fd" }}>
                    <Send sx={{ color: "#1976d2" }} />
                    <Typography variant="h5" fontWeight="bold">
                      {connections?.sentRequests?.total || 0}
                    </Typography>
                    <Typography variant="caption">Sent</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#f3e5f5" }}>
                    <Inbox sx={{ color: "#7b1fa2" }} />
                    <Typography variant="h5" fontWeight="bold">
                      {connections?.receivedRequests?.total || 0}
                    </Typography>
                    <Typography variant="caption">Received</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#e8f5e9" }}>
                    <CheckCircle sx={{ color: "#388e3c" }} />
                    <Typography variant="h5" fontWeight="bold">
                      {connections?.sentRequests?.accepted || 0}
                    </Typography>
                    <Typography variant="caption">Accepted</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#fff3e0" }}>
                    <AccessTime sx={{ color: "#f57c00" }} />
                    <Typography variant="h5" fontWeight="bold">
                      {connections?.sentRequests?.pending || 0}
                    </Typography>
                    <Typography variant="caption">Pending</Typography>
                  </Paper>
                </Grid>
              </Grid>
              <Box mt={2} textAlign="center">
                <Chip
                  icon={<ChatBubble />}
                  label={`${connections?.activeChats || 0} Active Chats`}
                  color="primary"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Chat Activity */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <ChatBubble sx={{ fontSize: 30, color: "#4caf50", mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Chat Activity
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#e8f5e9" }}>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {chatActivity?.totalMessages || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Messages
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Send sx={{ color: "#1976d2" }} />
                    <Typography variant="h5" fontWeight="bold">
                      {chatActivity?.messagesSent || 0}
                    </Typography>
                    <Typography variant="caption">Sent</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Inbox sx={{ color: "#7b1fa2" }} />
                    <Typography variant="h5" fontWeight="bold">
                      {chatActivity?.messagesReceived || 0}
                    </Typography>
                    <Typography variant="caption">Received</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Connection Requests Tables */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              <Send sx={{ mr: 1, verticalAlign: "middle" }} />
              Sent Requests ({connections?.sentRequests?.total || 0})
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connections?.sentRequests?.data?.slice(0, 10).map((req) => (
                    <TableRow key={req._id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar
                            src={getProfileImageUrl(req.toUser?.profileImageUrl)}
                            sx={{ width: 30, height: 30, mr: 1 }}
                          >
                            {req.toUser?.name?.charAt(0) || "U"}
                          </Avatar>
                          <Typography variant="body2">
                            {req.toUser?.firstName || req.toUser?.name || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.status}
                          size="small"
                          color={
                            req.status === "accepted" ? "success" :
                            req.status === "pending" ? "warning" :
                            req.status === "rejected" ? "error" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDate(req.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                  {(!connections?.sentRequests?.data || connections.sentRequests.data.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No sent requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              <Inbox sx={{ mr: 1, verticalAlign: "middle" }} />
              Received Requests ({connections?.receivedRequests?.total || 0})
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connections?.receivedRequests?.data?.slice(0, 10).map((req) => (
                    <TableRow key={req._id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar
                            src={getProfileImageUrl(req.fromUser?.profileImageUrl)}
                            sx={{ width: 30, height: 30, mr: 1 }}
                          >
                            {req.fromUser?.name?.charAt(0) || "U"}
                          </Avatar>
                          <Typography variant="body2">
                            {req.fromUser?.firstName || req.fromUser?.name || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.status}
                          size="small"
                          color={
                            req.status === "accepted" ? "success" :
                            req.status === "pending" ? "warning" :
                            req.status === "rejected" ? "error" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDate(req.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                  {(!connections?.receivedRequests?.data || connections.receivedRequests.data.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No received requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Transactions */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          <Payment sx={{ mr: 1, verticalAlign: "middle" }} />
          Recent Transactions
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>Plan</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment ID</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions?.map((txn) => (
                <TableRow key={txn._id}>
                  <TableCell>{txn.plan?.name || "N/A"}</TableCell>
                  <TableCell>₹{txn.amount}</TableCell>
                  <TableCell>
                    <Chip
                      label={txn.status}
                      size="small"
                      color={txn.status === "completed" ? "success" : "warning"}
                    />
                  </TableCell>
                  <TableCell>{txn.razorpayPaymentId || "N/A"}</TableCell>
                  <TableCell>{formatDate(txn.createdAt)}</TableCell>
                </TableRow>
              ))}
              {(!transactions || transactions.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No transactions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default MilanSathiUserDetails;
